import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import SEO from "@/components/seo";

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

const TermsOfService = ({ embedded = false }: { embedded?: boolean }) => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={embedded ? '' : 'min-h-screen'} style={{ background: PALETTE.brightSnow }}>
      <SEO
        title="Terms of Service"
        description="Read the BrandComply terms of service. Understand the rules and guidelines that govern your use of our AI-powered video compliance platform."
        path="/terms-of-service"
      />
      {!embedded && (
        <header style={{ background: PALETTE.gunmetal }} className="text-white sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div
                className="flex items-center space-x-1 cursor-pointer"
                onClick={() => navigate('/')}
              >
                <img src="/brandcomply_logo.png" alt="Brand Logo" className="h-6 w-auto" />
                <div className="text-xl font-bold" style={{ color: PALETTE.inchworm }}>
                  BrandComply
                </div>
              </div>

              <Button
                variant="outline"
                className="font-semibold w-fit rounded-full border-2 h-8 px-4 hover:scale-105 transition-transform transition-colors duration-200"
                style={{ borderColor: PALETTE.inchworm, color: PALETTE.inchworm, background: 'transparent' }}
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
          </div>
        </header>
      )}

      <main className={embedded ? '' : 'max-w-4xl mx-auto px-6 py-16'}>
        <div className="mb-12">
          <h1 className="text-4xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
            Terms of Service
          </h1>
          <p className="text-lg" style={{ color: `${PALETTE.gunmetal}70` }}>
            Last updated: February 24, 2026
          </p>
          <p className="text-lg mt-4" style={{ color: `${PALETTE.gunmetal}70` }}>
            Welcome to BrandComply (&ldquo;Service,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;). These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of our website, services, and applications. By registering an account or otherwise using the Service, you agree to be bound by these Terms.
          </p>
        </div>

        <div className="prose max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              1. B2B Use Only
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              BrandComply is a business-to-business (B2B) service. By creating an account, you represent and warrant that you are purchasing the Service for commercial, business, or professional purposes. Consumer protection laws (including the EU right of withdrawal) do not apply to this agreement.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              2. Accounts and Responsibilities
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              You must provide accurate and complete information when registering. You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              3. Use of the Service and AI Disclaimer
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              BrandComply utilizes artificial intelligence to analyze video content against brand guidelines.
            </p>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              <strong style={{ color: PALETTE.gunmetal }}>Assistive Tool:</strong> The Service is designed to assist with compliance and quality control, but it does not provide legal advice. AI can occasionally produce inaccurate results (&ldquo;hallucinations&rdquo;). You are solely responsible for the final review, approval, and publication of any content analyzed by BrandComply.
            </p>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              <strong style={{ color: PALETTE.gunmetal }}>Restrictions:</strong> You agree not to use the Service in violation of any applicable laws, attempt to reverse-engineer the Service, or upload content that infringes on third-party intellectual property or privacy rights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              4. Intellectual Property and User Content
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              <strong style={{ color: PALETTE.gunmetal }}>Our IP:</strong> All intellectual property rights in the Service (software, design, logos) are owned by BrandComply.
            </p>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              <strong style={{ color: PALETTE.gunmetal }}>Your Content:</strong> You retain all ownership rights to the videos and guidelines you upload. By uploading content, you grant us a limited, secure license to process that content solely for the purpose of providing the Service to you.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              5. Subscriptions and Fees
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              The Service is offered on a subscription basis. Fees are billed in advance and are non-refundable. Subscriptions automatically renew unless canceled prior to the next billing cycle. You may cancel at any time via your account settings. We reserve the right to change our pricing with prior notice to you.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              6. Disclaimer of Warranties
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              The Service is provided &ldquo;as is&rdquo; and &ldquo;as available.&rdquo; We expressly disclaim all warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not guarantee uninterrupted availability of the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              7. Limitation of Liability
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              To the maximum extent permitted by applicable law, BrandComply shall not be liable for any indirect, incidental, consequential, or punitive damages, or for any loss of profits, data, reputation, or business interruptions arising from your use of the Service or any AI-generated analysis.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              8. Termination
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              We may suspend or terminate your account if you violate these Terms. Upon termination, your right to use the Service will immediately cease.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              9. Governing Law and Jurisdiction
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              These Terms are governed by the laws of Switzerland, excluding the UN Convention on Contracts for the International Sale of Goods (CISG). Any disputes arising out of or relating to these Terms shall be subject to the exclusive jurisdiction of the courts of St. Gallen, Switzerland.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              10. Contact Us
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              For any questions regarding these Terms, please contact us at:{' '}
              <a href="mailto:info@brandcomply.ai" className="underline hover:no-underline" style={{ color: PALETTE.gunmetal }}>
                info@brandcomply.ai
              </a>
            </p>
          </section>
        </div>
      </main>

      {!embedded && (
        <footer className="py-12 text-white" style={{ background: PALETTE.gunmetal }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row items-start gap-8 md:gap-16">
              <div className="max-w-xs">
                <div
                  className="flex items-center space-x-1 cursor-pointer"
                  onClick={() => navigate('/')}
                >
                  <img src="/brandcomply_logo.png" alt="Brand Logo" className="h-6 w-auto" />
                  <div className="text-xl font-bold" style={{ color: PALETTE.inchworm }}>
                    BrandComply
                  </div>
                </div>
                <p className="text-white/70 mt-4">
                  Launch influencer & UGC video campaigns faster and on-brand.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: PALETTE.inchworm }}>Product</h3>
                <div className="space-y-3">
                  <button onClick={() => navigate('/')} className="block text-white/70 hover:text-white transition-colors text-left">How it Works</button>
                  <button onClick={() => navigate('/')} className="block text-white/70 hover:text-white transition-colors text-left">Pricing</button>
                  <button onClick={() => navigate('/api')} className="block text-white/70 hover:text-white transition-colors text-left">API</button>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: PALETTE.inchworm }}>Legal</h3>
                <div className="space-y-3">
                  <button onClick={() => navigate('/terms-of-service')} className="block text-white/70 hover:text-white transition-colors text-left">Terms of Service</button>
                  <button onClick={() => navigate('/privacy-policy')} className="block text-white/70 hover:text-white transition-colors text-left">Privacy Policy</button>
                  <button onClick={() => navigate('/imprint')} className="block text-white/70 hover:text-white transition-colors text-left">Imprint</button>
                </div>
              </div>
            </div>

            <div className="border-t border-white/20 mt-12 pt-8 text-center text-white/70">
              <p>&copy; 2026 BrandComply. All rights reserved.</p>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default TermsOfService;
